import { Fragment, ReactNode, useId } from 'react';
import { DragDropContext, Draggable, DropResult } from 'react-beautiful-dnd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripVertical, faPlus } from '@fortawesome/free-solid-svg-icons';
import { observer } from 'mobx-react-lite';

import PhoenixDroppable from 'components/base/PhoenixDroppable';

import './DetailList.scss';

interface DetailListProps<T> {
  readonly items: readonly T[];
  /** Stable identity for each item — used as the React key and the drag id. */
  readonly getKey: (item: T) => string;
  /**
   * Render one item. Receives its 0-based position, and a drag-handle node when
   * the list is reorderable (spread it onto the item's `DetailCard` `dragHandle`);
   * `undefined` otherwise.
   */
  readonly renderItem: (item: T, index: number, dragHandle: ReactNode) => ReactNode;
  /** Provide to make items reorderable by drag; called with source/destination indices. */
  readonly onReorder?: (fromIndex: number, toIndex: number) => void;
  /** Provide to show an add slot at the bottom of the list. */
  readonly onAdd?: () => void;
  /** Label for the add slot (e.g. "Add recurrence"). */
  readonly addLabel?: ReactNode;
}

/** The dashed add slot at the foot of a DetailList — where the next card appears. */
const AddDetail = ({ label, onClick }: { readonly label?: ReactNode; readonly onClick: () => void }) => (
  <button type="button" className="detail-list__add" onClick={onClick}>
    <FontAwesomeIcon icon={faPlus} />
    {label}
  </button>
);

/**
 * A vertical, gapped list of detail cards. Layout-only: the caller renders each
 * item (typically a `DetailCard`) via `renderItem`. Pass `onReorder` to make the
 * items drag-reorderable; without it the list is static (used for unordered
 * detail sets). The top/section card is not part of the list — it sits beside it.
 */
export const DetailList = observer(<T,>({ items, getKey, renderItem, onReorder, onAdd, addLabel }: DetailListProps<T>) => {
  const droppableId = useId();
  const add = onAdd !== undefined ? <AddDetail label={addLabel} onClick={onAdd} /> : null;

  // Snapshot the (possibly observable) list HERE, in the observer's own render, so MobX tracks membership/order. The
  // reorder branch below reads it inside a react-beautiful-dnd render-prop, which runs outside this reaction and would not
  // otherwise track the array — so appends/reorders wouldn't re-render.
  const entries = items.map((item, index) => ({ item, index, key: getKey(item) }));

  if (onReorder === undefined) {
    return (
      <div className="detail-list">
        {entries.map(({ item, index, key }) => (
          <Fragment key={key}>{renderItem(item, index, undefined)}</Fragment>
        ))}
        {add}
      </div>
    );
  }

  const handleDragEnd = (result: DropResult) => {
    if (result.destination) {
      onReorder(result.source.index, result.destination.index);
    }
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <PhoenixDroppable droppableId={droppableId}>
        {(provided) => (
          <div className="detail-list" ref={provided.innerRef} {...provided.droppableProps}>
            {entries.map(({ item, index, key }) => (
              <Draggable key={key} draggableId={key} index={index}>
                {(dragProvided) => (
                  <div ref={dragProvided.innerRef} {...dragProvided.draggableProps}>
                    {renderItem(
                      item,
                      index,
                      <span className="detail-list__handle" aria-label="Drag to reorder" {...dragProvided.dragHandleProps}>
                        <FontAwesomeIcon icon={faGripVertical} />
                      </span>
                    )}
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
            {add}
          </div>
        )}
      </PhoenixDroppable>
    </DragDropContext>
  );
});
